import React, { useState, useEffect } from 'react';
import Modal from '../../../../../components/common/Modal';
import Receipt from './Receipt';
import { useLanguage } from '../../../../../../../hooks/useLanguage';
import { pos as frPos } from '../../../../../../../locales/fr/pos';
import { pos as esPos } from '../../../../../../../locales/es/pos';
import { pos as arPos } from '../../../../../../../locales/ar/pos';
import useMyAppStore from '../../../../../../../stores/myAppStore';

const translations = {
  fr: frPos,
  es: esPos,
  ar: arPos
};

const CheckoutModal = ({ 
  isOpen, 
  onClose, 
  currentCommande, 
  onPaymentComplete,
  onSaveSale 
}) => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const { currentUser } = useMyAppStore();

  // États pour le paiement
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountReceived, setAmountReceived] = useState(0);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // État pour afficher le ticket de caisse
  const [showReceipt, setShowReceipt] = useState(false);
  const [savedSaleData, setSavedSaleData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);

  // Calculer le montant total et le reste
  const totalAmount = currentCommande?.montant?.total || 0;
  const change = amountReceived - totalAmount;

  // Méthodes de paiement disponibles
  const paymentMethods = [
    { id: 'cash', label: 'Espèces', icon: '💵' },
    { id: 'card', label: 'Carte bancaire', icon: '💳' },
    { id: 'check', label: 'Chèque', icon: '📄' },
    { id: 'transfer', label: 'Virement', icon: '🏦' },
    { id: 'mobile', label: 'Mobile Money', icon: '📱' }
  ];

  // Réinitialiser les champs quand le modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      setAmountReceived(totalAmount);
      setCustomerName(currentCommande?.customer?.name || '');
      setCustomerPhone(currentCommande?.customer?.phone || '');
      setNotes('');
      setPaymentMethod('cash');
    }
  }, [isOpen, totalAmount, currentCommande]);

  // Gérer la saisie du montant reçu
  const handleAmountReceivedChange = (value) => {
    const numValue = parseFloat(value) || 0;
    setAmountReceived(numValue);
  };

  // Finaliser le paiement
  const handlePaymentComplete = async () => {
    // Vérifier si le montant est insuffisant
    if (amountReceived < totalAmount) {
      const remainingAmount = totalAmount - amountReceived;
      const confirmMessage = t.checkout?.errors?.insufficientAmountConfirm || 
        `Le montant reçu (${amountReceived.toFixed(2)} €) est inférieur au total (${totalAmount.toFixed(2)} €).\n\nReste à payer : ${remainingAmount.toFixed(2)} €\n\nVoulez-vous finaliser la vente en crédit ?`;
      
      const userConfirmed = window.confirm(confirmMessage);
      
      if (!userConfirmed) {
        return; // L'utilisateur a annulé
      }
      
      // L'utilisateur a confirmé, on continue avec le crédit
      console.log('💳 Finalisation en crédit confirmée par l\'utilisateur');
    }

    setIsProcessing(true);

    try {
      // 1. Préparer les données de paiement pour la vente
      const paymentDataObj = {
        paymentId: 0, // Toujours 0, pas de référence
        method: paymentMethod,
        amount: amountReceived, // Montant effectivement reçu
        amountReceived: amountReceived,
        change: change,
        customerName: customerName,
        customerPhone: customerPhone,
        notes: notes,
        datePaiement: new Date(),
        status: amountReceived >= totalAmount ? 'completed' : 'partial' // Statut selon le montant
      };

      // 2. Préparer les données de vente complètes (sans id)
      const { id, ...saleDataWithoutId } = currentCommande;
      const saleData = {
        ...saleDataWithoutId,
        customer: {
          ...currentCommande.customer,
          name: customerName,
          phone: customerPhone
        },
        paiement: paymentDataObj,
        status: amountReceived >= totalAmount ? 'completed' : 'pending', // Statut de la vente
        notes: notes + (amountReceived < totalAmount ? `\n[CRÉDIT] Reste à payer: ${(totalAmount - amountReceived).toFixed(2)} €` : ''),
        updatedAt: new Date()
      };

      // 3. Préparer les données de paiement pour la transaction
      const paymentData = {
        userId: currentUser?.id || null, // Utiliser l'ID de l'utilisateur connecté ou null
        amount: amountReceived,
        method: paymentMethod,
        type: 'payment',
        status: amountReceived >= totalAmount ? 'completed' : 'partial',
        reference: `PAY-${saleData.num || 'SALE'}`,
        notes: notes + (amountReceived < totalAmount ? ` (Paiement partiel - Crédit: ${(totalAmount - amountReceived).toFixed(2)} €)` : '')
      };

      // 4. Sauvegarder la vente ET le paiement dans une transaction atomique
      let savedSale = null;
      let savedPayment = null;
      
      if (onSaveSale) {
        // Utiliser la nouvelle méthode avec transaction
        const result = await onSaveSale(saleData, paymentData);
        savedSale = result.sale;
        savedPayment = result.payment;
      }

      // Sauvegarder les données pour le ticket
      setSavedSaleData(savedSale || saleData);
      setPaymentData(paymentDataObj);

      // Notifier le paiement complet
      if (onPaymentComplete) {
        onPaymentComplete(paymentDataObj);
      }

      // Afficher le ticket de caisse
      setShowReceipt(true);

    } catch (error) {
      console.error('Erreur lors du paiement:', error);
      alert(t.checkout?.errors?.paymentError || 'Erreur lors du paiement. Veuillez réessayer.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Raccourcis pour les montants courants
  const quickAmounts = [5, 10, 20, 50, 100, 200];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={showReceipt ? (t.receipt?.title || "Ticket de Caisse") : (t.checkout?.title || "Finaliser le Paiement")}
      width="max-w-4xl"
    >
      {showReceipt ? (
        // Affichage du ticket de caisse
        <Receipt
          saleData={savedSaleData}
          paymentData={paymentData}
          onClose={() => {
            setShowReceipt(false);
            onClose();
          }}
          onPrint={() => {
            // TODO: Implémenter l'impression
            console.log('Impression du ticket...');
          }}
        />
      ) : (
        // Formulaire de paiement
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Colonne gauche - Détails de la commande */}
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-lg mb-3">{t.checkout?.orderSummary?.title || 'Récapitulatif de la commande'}</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>{t.checkout?.orderSummary?.subtotal || 'Sous-total'}:</span>
                  <span>{(totalAmount - (currentCommande?.montant?.tva || 0)).toFixed(2)} €</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.checkout?.orderSummary?.tva || 'TVA'}:</span>
                  <span>{(currentCommande?.montant?.tva || 0).toFixed(2)} €</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>{t.checkout?.orderSummary?.totalToPay || 'Total à payer'}:</span>
                  <span className="text-green-600">{totalAmount.toFixed(2)} €</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.checkout?.customer?.name || 'Nom du client'}
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t.checkout?.customer?.namePlaceholder || 'Nom du client'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.checkout?.customer?.phone || 'Téléphone'}
                </label>
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t.checkout?.customer?.phonePlaceholder || 'Numéro de téléphone'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.checkout?.customer?.notes || 'Notes'}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t.checkout?.customer?.notesPlaceholder || 'Notes additionnelles...'}
                />
              </div>
            </div>
          </div>

          {/* Colonne droite - Paiement */}
          <div className="space-y-4">
            {/* Méthode de paiement */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t.checkout?.paymentMethods?.title || 'Méthode de paiement'}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {paymentMethods.map((method) => (
                  <button
                    key={method.id}
                    onClick={() => setPaymentMethod(method.id)}
                    className={`p-3 border rounded-lg text-center transition-colors ${
                      paymentMethod === method.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="text-2xl mb-1">{method.icon}</div>
                    <div className="text-sm font-medium">{t.checkout?.paymentMethods?.[method.id] || method.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Montant reçu */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.checkout?.amountReceived?.title || 'Montant reçu'}
              </label>
              <div className="space-y-2">
                <input
                  type="number"
                  value={amountReceived}
                  onChange={(e) => handleAmountReceivedChange(e.target.value)}
                  className="w-full px-3 py-3 text-xl font-bold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t.checkout?.amountReceived?.placeholder || '0.00'}
                  step="0.01"
                  min="0"
                />
                
                {/* Raccourcis pour les montants courants */}
                <div className="flex flex-wrap gap-2">
                  {quickAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setAmountReceived(amount)}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      {amount}€
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Monnaie */}
            {change > 0 && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-green-800">{t.checkout?.amountReceived?.change || 'Monnaie à rendre'}:</span>
                  <span className="text-xl font-bold text-green-600">{change.toFixed(2)} €</span>
                </div>
              </div>
            )}

            {change < 0 && (
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-red-800">{t.checkout?.amountReceived?.insufficient || 'Montant insuffisant'}:</span>
                  <span className="text-xl font-bold text-red-600">{Math.abs(change).toFixed(2)} €</span>
                </div>
              </div>
            )}

            {/* Boutons d'action */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isProcessing}
              >
                {t.checkout?.actions?.cancel || 'Annuler'}
              </button>
              <button
                onClick={handlePaymentComplete}
                disabled={isProcessing}
                className={`flex-1 px-4 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  amountReceived >= totalAmount 
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'bg-orange-600 text-white hover:bg-orange-700'
                }`}
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t.checkout?.actions?.processing || 'Traitement...'}
                  </span>
                ) : (
                  <span>
                    {amountReceived >= totalAmount 
                      ? `${t.checkout?.actions?.finalize || 'Finaliser'} (${totalAmount.toFixed(2)} €)`
                      : `${t.checkout?.actions?.finalizeCredit || 'Finaliser en Crédit'} (${amountReceived.toFixed(2)} € / ${totalAmount.toFixed(2)} €)`
                    }
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CheckoutModal; 